import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    type IconDefinition,
    Label,
    renderIcon,
    stopPropagationProps,
} from '@gitbook/spine-react';

import { sizes } from '../constants';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';
import { normalizeStyle, type StyleProp, type ViewStyle } from '../web';

export type CheckBoxProps = {
    multi?: boolean;
    size?: number;
    value: boolean;
    onValueChange: (value: boolean) => void;
    readOnly?: boolean;
    disabled?: boolean;

    /** Stops pointer and click events from bubbling to parent interactive rows. */
    stopPropagation?: boolean;

    label?: React.ReactNode;
    labelIcon?: IconDefinition;
    labelDescription?: React.ReactNode;

    style?: StyleProp<ViewStyle>;
};

/**
 * Checkbox is shown as a square box that is ticked (checked) when activated.
 */
export function CheckBox(props: CheckBoxProps): React.ReactElement {
    const { label, labelIcon, labelDescription, value, onValueChange, stopPropagation, style } =
        props;

    const id = React.useId();
    const inputID = `checkbox-input-${id}`;
    const labelID = label ? `checkbox-label-${id}` : undefined;

    const checkbox = <Checkbox {...props} inputID={inputID} labelID={labelID} />;

    if (!label) {
        return checkbox;
    }

    function onValueToggle(event?: React.SyntheticEvent<HTMLElement>) {
        if (stopPropagation) {
            event?.stopPropagation();
        }

        onValueChange(!value);
    }

    return (
        <div className="flex items-center" style={normalizeStyle(style)}>
            <div className="mr-4 flex">{checkbox}</div>
            <div className="min-w-0 flex-1">
                <div className={labelDescription ? 'mb-1 flex items-center' : 'flex items-center'}>
                    {labelIcon ? renderIcon(labelIcon, { className: 'mr-2', size: 18 }) : null}
                    <Label onClick={onValueToggle} id={labelID}>
                        {label}
                    </Label>
                </div>
                {labelDescription ? (
                    <div>
                        <Description onClick={onValueToggle}>{labelDescription}</Description>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function Checkbox(
    props: CheckBoxProps & {
        inputID: string;
        labelID: string | undefined;
    }
) {
    const {
        readOnly,
        disabled,
        value,
        onValueChange,
        size = sizes.ICON_400,
        inputID,
        labelID,
        multi = true,
        stopPropagation = false,
    } = props;

    const [, styles] = useCurrentTheme(themedStyles);

    if (readOnly || disabled) {
        return (
            <div
                className="flex shrink-0 items-center justify-center"
                style={normalizeStyle([
                    styles.checkbox,
                    multi ? null : styles.radio,
                    value && styles.checkboxChecked,
                    disabled && styles.disabled,
                    !value && disabled && styles.checkboxDisabledUnchecked,
                    {
                        width: size,
                        height: size,
                        borderRadius: multi ? undefined : size,
                    },
                ])}
            >
                {value ? <Icon.Check size={size * 0.9} style={styles.icon} /> : null}
            </div>
        );
    }

    return (
        <button
            type="button"
            className="flex shrink-0 appearance-none items-center justify-center border-0 bg-transparent p-0"
            {...(stopPropagation ? stopPropagationProps : undefined)}
            style={normalizeStyle([
                styles.checkbox,
                styles.checkboxEditable,
                value && styles.checkboxChecked,
                multi ? null : styles.radio,
                {
                    width: size,
                    height: size,
                    borderRadius: multi ? undefined : size,
                },
            ])}
            onClick={(event) => {
                if (stopPropagation) {
                    event.stopPropagation();
                }

                onValueChange(!value);
            }}
            role="checkbox"
            aria-labelledby={labelID}
            aria-checked={value}
            id={inputID}
        >
            {value ? <Icon.Check size={size * 0.7} style={styles.icon} aria-hidden /> : null}
        </button>
    );
}

const themedStyles = ThemedStyleSheet.create({
    icon: () => ({
        color: 'var(--sp-color-text-white)',
        strokeWidth: 3,
    }),
    checkbox: (theme) => ({
        borderRadius: theme.radiuses.medium,
        borderColor: 'var(--sp-color-border-base)',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
    }),
    radio: () => ({
        borderWidth: 2,
    }),
    disabled: () => ({
        opacity: 0.38,
    }),
    checkboxEditable: {
        cursor: 'pointer',
    } as ViewStyle,
    checkboxChecked: () => ({
        backgroundColor: 'var(--sp-color-bg-form-radio-checked-default)',
        borderColor: 'var(--sp-color-border-form-radio-checked-default)',
    }),
    checkboxDisabledUnchecked: () => ({
        backgroundColor: 'var(--sp-color-bg-inactive)',
        borderColor: 'var(--sp-color-border-form-radio-unchecked-disabled)',
    }),
});
